import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { createSongV2, CreateSongV2Data } from "./songV2Api";
import styles from "./SongV2Shared.module.css";
import {
  Music,
  ArrowLeft,
  Save,
  AlertCircle,
  Youtube,
  Clock,
  Zap,
  Volume2,
} from "lucide-react";

export default function AddSongV2() {
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [artist, setArtist] = useState("");
  const [bpm, setBpm] = useState(120);
  const [ts, setTs] = useState("4/4");
  const [durationMs, setDurationMs] = useState(180000);
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [videoId, setVideoId] = useState("");
  const [syncMs, setSyncMs] = useState(0);
  const [lookaheadMs, setLookaheadMs] = useState(2200);
  const [hitMs, setHitMs] = useState(80);
  const [calibrateSpeed, setCalibrateSpeed] = useState(1.0);
  const [isLocked, setIsLocked] = useState(false);
  const [lanesRaw, setLanesRaw] = useState<string>("[]");
  const [dtRaw, setDtRaw] = useState<string>("[]");
  const [mRaw, setMRaw] = useState<string>("[]");
  const [chartRaw, setChartRaw] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const extractVideoId = (url: string): string => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/,
      /^([a-zA-Z0-9_-]{11})$/,
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return url;
  };

  const handleYoutubeUrlChange = (url: string) => {
    setYoutubeUrl(url);
    const extracted = extractVideoId(url);
    setVideoId(extracted);
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!title.trim()) newErrors.title = "Başlık zorunludur";
    if (!artist.trim()) newErrors.artist = "Sanatçı zorunludur";
    if (!ts.trim()) newErrors.ts = "Time signature zorunludur";
    if (!videoId.trim()) newErrors.videoId = "YouTube Video ID zorunludur";
    if (bpm < 20 || bpm > 300) newErrors.bpm = "BPM 20-300 arasında olmalıdır";
    if (durationMs < 1000) newErrors.durationMs = "Süre en az 1 saniye olmalıdır";
    if (calibrateSpeed < 0.25 || calibrateSpeed > 2.0)
      newErrors.calibrateSpeed = "Calibrate speed 0.25-2.0 arasında olmalıdır";

    // Validate JSON fields
    if (lanesRaw.trim()) {
      try {
        const lanes = JSON.parse(lanesRaw);
        if (!Array.isArray(lanes)) {
          newErrors.lanesRaw = "Lanes bir dizi (array) olmalıdır";
        }
      } catch (err) {
        newErrors.lanesRaw = "Geçerli JSON formatında yazın";
      }
    } else {
      newErrors.lanesRaw = "Lanes alanı zorunludur";
    }

    if (dtRaw.trim()) {
      try {
        const dt = JSON.parse(dtRaw);
        if (!Array.isArray(dt)) {
          newErrors.dtRaw = "DT bir dizi (array) olmalıdır";
        }
      } catch (err) {
        newErrors.dtRaw = "Geçerli JSON formatında yazın";
      }
    } else {
      newErrors.dtRaw = "DT alanı zorunludur";
    }

    if (mRaw.trim()) {
      try {
        const m = JSON.parse(mRaw);
        if (!Array.isArray(m)) {
          newErrors.mRaw = "M bir dizi (array) olmalıdır";
        }
      } catch (err) {
        newErrors.mRaw = "Geçerli JSON formatında yazın";
      }
    } else {
      newErrors.mRaw = "M alanı zorunludur";
    }

    if (chartRaw.trim()) {
      try {
        const chart = JSON.parse(chartRaw);
        if (!chart.format || !chart.ppq || !chart.tempo_map || !chart.events) {
          newErrors.chartRaw =
            "Chart format, ppq, tempo_map ve events alanlarına sahip olmalıdır";
        }
      } catch (err) {
        newErrors.chartRaw = "Geçerli JSON formatında yazın";
      }
    } else {
      newErrors.chartRaw = "Chart alanı zorunludur";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    let lanes, dt, m, chart;
    try {
      lanes = JSON.parse(lanesRaw);
      dt = JSON.parse(dtRaw);
      m = JSON.parse(mRaw);
      chart = JSON.parse(chartRaw);
    } catch (err) {
      setErrors({ general: "JSON formatları hatalı" });
      setIsSubmitting(false);
      return;
    }

    try {
      const data: CreateSongV2Data = {
        v: 1,
        title: title.trim(),
        artist: artist.trim(),
        bpm,
        ts: ts.trim(),
        duration_ms: durationMs,
        source: {
          type: "youtube",
          url: youtubeUrl.trim() || `https://www.youtube.com/watch?v=${videoId.trim()}`,
          video_id: videoId.trim(),
        },
        sync_ms: syncMs,
        lookahead_ms: lookaheadMs,
        hit_ms: hitMs,
        calibrate_speed: calibrateSpeed,
        lanes,
        dt,
        m,
        chart,
        is_locked: isLocked,
      };

      await createSongV2(data);
      navigate("/songsv2");
    } catch (error: any) {
      console.error("Song creation error:", error);

      if (error.response?.status === 401) {
        setErrors({
          general: "Oturum süreniz dolmuş. Lütfen tekrar giriş yapın.",
        });
      } else if (error.response?.status === 403) {
        setErrors({
          general: "Bu işlem için admin yetkisi gerekiyor.",
        });
      } else if (error.response?.status === 400) {
        setErrors({
          general: "Gönderilen veriler hatalı. Lütfen kontrol edin.",
        });
      } else if (error.response?.status >= 500) {
        setErrors({
          general: "Sunucu hatası. Lütfen daha sonra tekrar deneyin.",
        });
      } else {
        setErrors({
          general: "Şarkı eklenirken hata oluştu. Lütfen tekrar deneyin.",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleExampleData = () => {
    const exampleLanes = [
      { lane: 0, name: "Hi-Hat", midi_notes: [42, 44] },
      { lane: 1, name: "Kick", midi_notes: [36] },
      { lane: 2, name: "Snare", midi_notes: [38, 40] },
      { lane: 3, name: "Tom 1", midi_notes: [48] },
      { lane: 4, name: "Tom 2", midi_notes: [47] },
      { lane: 5, name: "Tom 3", midi_notes: [45] },
      { lane: 6, name: "Crash", midi_notes: [49] },
      { lane: 7, name: "Ride", midi_notes: [51] },
    ];

    const exampleChart = {
      format: "score_v2",
      ppq: 480,
      tempo_map: [{ tick: 0, bpm: 120 }],
      events: [
        { t0: 0, dt: 240, m: 1 },
        { t0: 480, dt: 240, m: 2 },
      ],
    };

    setLanesRaw(JSON.stringify(exampleLanes, null, 2));
    setDtRaw(JSON.stringify([0, 500, 1000], null, 2));
    setMRaw(JSON.stringify([1, 2, 4], null, 2));
    setChartRaw(JSON.stringify(exampleChart, null, 2));
  };

  return (
    <div className={styles.container}>
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>
            <Music className={styles.titleIcon} />
            Yeni Şarkı Ekle (V2)
          </h1>
          <p className={styles.subtitle}>Yeni bir drum chart ekleyin</p>
        </div>
        <div className={styles.headerActions}>
          <Link to="/songsv2" className={styles.secondaryButton}>
            <ArrowLeft size={16} />
            Geri Dön
          </Link>
        </div>
      </div>

      {errors.general && (
        <div className={styles.errorAlert}>
          <AlertCircle size={20} />
          <span>{errors.general}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <Music size={20} />
            <h2>Temel Bilgiler</h2>
          </div>
          <div className={styles.cardBody}>
            <div className={styles.grid2}>
              <div>
                <label className={styles.label}>
                  Başlık <span className={styles.required}>*</span>
                </label>
                <input
                  className={styles.input}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Şarkı başlığı"
                />
                {errors.title && <span className={styles.error}>{errors.title}</span>}
              </div>
              <div>
                <label className={styles.label}>
                  Sanatçı <span className={styles.required}>*</span>
                </label>
                <input
                  className={styles.input}
                  value={artist}
                  onChange={(e) => setArtist(e.target.value)}
                  placeholder="Sanatçı adı"
                />
                {errors.artist && <span className={styles.error}>{errors.artist}</span>}
              </div>
            </div>

            <div className={styles.grid3}>
              <div>
                <label className={styles.label}>
                  BPM <span className={styles.required}>*</span>
                </label>
                <input
                  type="number"
                  className={styles.input}
                  value={bpm}
                  onChange={(e) => setBpm(parseInt(e.target.value) || 120)}
                  min={20}
                  max={300}
                />
                {errors.bpm && <span className={styles.error}>{errors.bpm}</span>}
              </div>
              <div>
                <label className={styles.label}>
                  Time Signature <span className={styles.required}>*</span>
                </label>
                <input
                  className={styles.input}
                  value={ts}
                  onChange={(e) => setTs(e.target.value)}
                  placeholder="4/4"
                />
                {errors.ts && <span className={styles.error}>{errors.ts}</span>}
              </div>
              <div>
                <label className={styles.label}>
                  Süre (ms) <span className={styles.required}>*</span>
                </label>
                <input
                  type="number"
                  className={styles.input}
                  value={durationMs}
                  onChange={(e) => setDurationMs(parseInt(e.target.value) || 180000)}
                  min={1000}
                />
                {errors.durationMs && <span className={styles.error}>{errors.durationMs}</span>}
              </div>
            </div>
          </div>
        </div>

        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <Youtube size={20} />
            <h2>YouTube Kaynağı</h2>
          </div>
          <div className={styles.cardBody}>
            <div>
              <label className={styles.label}>
                YouTube URL veya Video ID <span className={styles.required}>*</span>
              </label>
              <input
                className={styles.input}
                value={youtubeUrl}
                onChange={(e) => handleYoutubeUrlChange(e.target.value)}
                placeholder="https://www.youtube.com/watch?v=dQw4w9WgXcQ veya dQw4w9WgXcQ"
              />
              {videoId && (
                <p className={styles.hint}>Tespit edilen Video ID: {videoId}</p>
              )}
              {errors.videoId && <span className={styles.error}>{errors.videoId}</span>}
            </div>
          </div>
        </div>

        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <Clock size={20} />
            <h2>Zamanlama Ayarları</h2>
          </div>
          <div className={styles.cardBody}>
            <div className={styles.grid4}>
              <div>
                <label className={styles.label}>Sync (ms)</label>
                <input
                  type="number"
                  className={styles.input}
                  value={syncMs}
                  onChange={(e) => setSyncMs(parseInt(e.target.value) || 0)}
                />
              </div>
              <div>
                <label className={styles.label}>Lookahead (ms)</label>
                <input
                  type="number"
                  className={styles.input}
                  value={lookaheadMs}
                  onChange={(e) => setLookaheadMs(parseInt(e.target.value) || 2200)}
                />
              </div>
              <div>
                <label className={styles.label}>Hit Window (ms)</label>
                <input
                  type="number"
                  className={styles.input}
                  value={hitMs}
                  onChange={(e) => setHitMs(parseInt(e.target.value) || 80)}
                />
              </div>
              <div>
                <label className={styles.label}>Calibrate Speed</label>
                <input
                  type="number"
                  step="0.1"
                  className={styles.input}
                  value={calibrateSpeed}
                  onChange={(e) => setCalibrateSpeed(parseFloat(e.target.value) || 1.0)}
                  min={0.25}
                  max={2.0}
                />
                {errors.calibrateSpeed && (
                  <span className={styles.error}>{errors.calibrateSpeed}</span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <Zap size={20} />
            <h2>Chart Data</h2>
            <button
              type="button"
              onClick={handleExampleData}
              className={styles.secondaryButton}
              style={{ marginLeft: "auto" }}
            >
              Örnek Veri Yükle
            </button>
          </div>
          <div className={styles.cardBody}>
            <div>
              <label className={styles.label}>
                Lanes <span className={styles.required}>*</span>
              </label>
              <textarea
                className={styles.textarea}
                value={lanesRaw}
                onChange={(e) => setLanesRaw(e.target.value)}
                rows={8}
                placeholder='[{"lane": 0, "name": "Hi-Hat", "midi_notes": [42, 44]}]'
              />
              {errors.lanesRaw && <span className={styles.error}>{errors.lanesRaw}</span>}
              <p className={styles.hint}>
                8 lane için JSON array (lane: 0-7, name, midi_notes)
              </p>
            </div>

            <div className={styles.grid2}>
              <div>
                <label className={styles.label}>
                  Delta Time (dt) <span className={styles.required}>*</span>
                </label>
                <textarea
                  className={styles.textarea}
                  value={dtRaw}
                  onChange={(e) => setDtRaw(e.target.value)}
                  rows={6}
                  placeholder="[0, 500, 1000, 1500]"
                />
                {errors.dtRaw && <span className={styles.error}>{errors.dtRaw}</span>}
                <p className={styles.hint}>Delta time array (milliseconds)</p>
              </div>
              <div>
                <label className={styles.label}>
                  Mask (m) <span className={styles.required}>*</span>
                </label>
                <textarea
                  className={styles.textarea}
                  value={mRaw}
                  onChange={(e) => setMRaw(e.target.value)}
                  rows={6}
                  placeholder="[1, 2, 4, 8]"
                />
                {errors.mRaw && <span className={styles.error}>{errors.mRaw}</span>}
                <p className={styles.hint}>Mask array (bit mask for lanes)</p>
              </div>
            </div>

            <div>
              <label className={styles.label}>
                Chart <span className={styles.required}>*</span>
              </label>
              <textarea
                className={styles.textarea}
                value={chartRaw}
                onChange={(e) => setChartRaw(e.target.value)}
                rows={12}
                placeholder='{"format": "score_v2", "ppq": 480, "tempo_map": [...], "events": [...]}'
              />
              {errors.chartRaw && <span className={styles.error}>{errors.chartRaw}</span>}
              <p className={styles.hint}>
                Chart object (format, ppq, tempo_map, events)
              </p>
            </div>
          </div>
        </div>

        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <Volume2 size={20} />
            <h2>Diğer Ayarlar</h2>
          </div>
          <div className={styles.cardBody}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={isLocked}
                onChange={(e) => setIsLocked(e.target.checked)}
              />
              <span>Kilitli (Kullanıcılar değiştiremez)</span>
            </label>
          </div>
        </div>

        <div className={styles.formActions}>
          <button
            type="button"
            onClick={() => navigate("/songsv2")}
            className={styles.secondaryButton}
            disabled={isSubmitting}
          >
            İptal
          </button>
          <button
            type="submit"
            className={styles.primaryButton}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <div className={styles.spinner}></div>
                Kaydediliyor...
              </>
            ) : (
              <>
                <Save size={16} />
                Kaydet
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
